import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Cross } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChristianAdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      const { access_token, user } = response.data;

      if (!user?.is_christian_admin && !user?.is_admin) {
        toast.error('This account is not authorized as a Christian Admin');
        setLoading(false);
        return;
      }

      localStorage.setItem('littlelearners-token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      toast.success(`Welcome, ${user.name || 'Admin'}!`);
      navigate('/christian/admin');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-amber-300 hover:text-amber-200 transition-colors mb-4">
            <Cross className="w-6 h-6" />
            <span className="font-bold text-2xl tracking-wide">Zazu Christian</span>
          </Link>
          <p className="text-purple-200 text-sm">Christian Education Admin Console</p>
        </div>

        <Card className="bg-slate-800/80 border-purple-700 backdrop-blur-sm shadow-2xl">
          <CardHeader className="text-center">
            <CardTitle className="text-white text-2xl">Admin Sign In</CardTitle>
            <CardDescription className="text-purple-200">
              Manage Christian curriculum, users, and worship songs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-purple-100">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="christian_admin@zazued.com"
                  required
                  className="mt-1 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                  data-testid="christian-admin-email"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-purple-100">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="bg-slate-700 border-slate-600 text-white pr-10"
                    data-testid="christian-admin-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                data-testid="christian-admin-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Sign In as Admin
              </Button>
            </form>
            <p className="text-center text-purple-300 text-xs mt-6">
              Need general access? <Link to="/login" className="underline hover:text-amber-300">Use parent login</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChristianAdminLogin;
